import { formatMoney, type Money, money, notional, price, qty, ZERO_MONEY } from "@atc/domain";
import type { AccountSnapshot } from "./stream";

/**
 * Mark a portfolio to the latest prices, between server snapshots.
 *
 * **This is the one place the terminal computes a number rather than printing
 * one.** The rule it has to survive is in PRODUCT.md: *the terminal must never
 * compute something the API does not expose.* It does not, in two senses:
 *
 * 1. `GET /api/v1/accounts/:id` already returns exactly this — `equity = cash +
 *    Σ qty × mark`. The browser is recomputing a figure the API also serves, not
 *    inventing one.
 * 2. It uses **the same functions the server does**. `notional()` from
 *    `@atc/domain` is what `AccountDO.snapshot` calls. Two implementations of
 *    the same arithmetic would eventually disagree in the last decimal place;
 *    one implementation cannot.
 *
 * And it is integer arithmetic throughout, because `@atc/domain` is scaled
 * integers throughout. Marking a portfolio in floats is exactly the kind of
 * shortcut the money rules exist to forbid — see .agent/LESSONS.md.
 *
 * Why compute it at all rather than ask the server: `ARCH-overview`'s scaling
 * invariant. Market-data work stays O(distinct symbols) because 10K browsers
 * marking their own positions from one broadcast price is free, where 10K
 * server-side valuations per tick is not.
 */
export function markPortfolio(
  snapshot: AccountSnapshot,
  marks: ReadonlyMap<string, string>,
): { equity: string; marketValue: string; marked: number } {
  let marketValue = ZERO_MONEY;
  let marked = 0;

  for (const position of snapshot.positions) {
    const held = qty(position.qty);
    const latest = marks.get(position.symbol);

    if (latest !== undefined) {
      marketValue = (marketValue + notional(held, price(latest))) as Money;
      marked++;
      continue;
    }

    // No live price for this symbol: fall back to whatever the server last
    // said it was worth. Dropping it would make equity *fall* the moment a
    // position's symbol stopped ticking, which reads as a loss that never
    // happened.
    if (position.market_value !== undefined) {
      marketValue = (marketValue + money(position.market_value)) as Money;
    } else if (position.mark !== undefined) {
      marketValue = (marketValue + notional(held, price(position.mark))) as Money;
    }
  }

  return {
    equity: formatMoney((money(snapshot.cash) + marketValue) as Money),
    marketValue: formatMoney(marketValue),
    marked,
  };
}
